import { HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { of } from 'rxjs';
import { catchError, map, mergeMap, switchMap, withLatestFrom } from 'rxjs/operators';
import { DailyLineAllocationOverlapDto } from '../../features/daily-line-schedule/models/daily-line-schedule.models';
import { DailyLineScheduleApiService } from '../../features/daily-line-schedule/services/daily-line-schedule-api.service';
import { userFacingApiError } from '../master-schedule/master-schedule-user-errors';
import { ToastActions } from '../toast/toast.actions';
import { DailyLineScheduleActions } from './daily-line-schedule.actions';
import {
  selectDailyLineScheduleQuery,
  selectDailyLineScheduleSearch,
} from './daily-line-schedule.selectors';

@Injectable()
export class DailyLineScheduleEffects {
  private readonly actions$ = inject(Actions);
  private readonly api = inject(DailyLineScheduleApiService);
  private readonly store = inject(Store);

  loadAccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DailyLineScheduleActions.loadAccess),
      switchMap(() =>
        this.api.getAccess().pipe(
          map((bases) => DailyLineScheduleActions.loadAccessSuccess({ bases })),
          catchError((error) => {
            console.error(error);
            return of(
              DailyLineScheduleActions.loadAccessFailure({ error: userFacingApiError(error) })
            );
          })
        )
      )
    )
  );

  load$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DailyLineScheduleActions.load),
      switchMap(({ startDate, endDate, search }) =>
        this.api.getCalendar(startDate, endDate).pipe(
          map((calendar) =>
            DailyLineScheduleActions.loadSuccess({
              calendar,
              search: search ?? '',
            })
          ),
          catchError((error) => {
            console.error(error);
            const message = userFacingApiError(error);
            return of(
              DailyLineScheduleActions.loadFailure({ error: message }),
              ToastActions.showToast({
                severity: 'error',
                summary: 'Daily line scheduling',
                detail: message,
              })
            );
          })
        )
      )
    )
  );

  refresh$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DailyLineScheduleActions.refresh),
      withLatestFrom(
        this.store.select(selectDailyLineScheduleQuery),
        this.store.select(selectDailyLineScheduleSearch)
      ),
      map(([, query, search]) => {
        if (!query) {
          return DailyLineScheduleActions.loadFailure({
            error: 'No schedule query available to refresh',
          });
        }
        return DailyLineScheduleActions.load({
          startDate: query.startDate,
          endDate: query.endDate,
          search,
        });
      })
    )
  );

  loadLines$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DailyLineScheduleActions.loadLines),
      switchMap(() =>
        this.api.listLines().pipe(
          map((lines) => DailyLineScheduleActions.loadLinesSuccess({ lines })),
          catchError((error) => {
            console.error(error);
            return of(DailyLineScheduleActions.loadFailure({ error: userFacingApiError(error) }));
          })
        )
      )
    )
  );

  loadShifts$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DailyLineScheduleActions.loadShifts),
      mergeMap(({ base }) =>
        this.api.listShifts(base).pipe(
          map((shifts) => DailyLineScheduleActions.loadShiftsSuccess({ base, shifts })),
          catchError((error) => {
            console.error(error);
            return of(DailyLineScheduleActions.loadFailure({ error: userFacingApiError(error) }));
          })
        )
      )
    )
  );

  loadEligibleCrew$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DailyLineScheduleActions.loadEligibleCrew),
      switchMap(({ base, dateFrom, dateTo }) =>
        this.api.listEligibleCrew(base, dateFrom, dateTo).pipe(
          map((crew) => DailyLineScheduleActions.loadEligibleCrewSuccess({ crew })),
          catchError((error) => {
            console.error(error);
            return of(DailyLineScheduleActions.loadEligibleCrewSuccess({ crew: [] }));
          })
        )
      )
    )
  );

  createLine$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DailyLineScheduleActions.createLine),
      mergeMap(({ name }) =>
        this.api.createLine(name).pipe(
          mergeMap((line) =>
            of(
              DailyLineScheduleActions.createLineSuccess({ line }),
              ToastActions.showToast({
                severity: 'success',
                summary: 'Daily line scheduling',
                detail: 'Line added',
              })
            )
          ),
          catchError((error) => {
            console.error(error);
            const message = userFacingApiError(error);
            return of(
              DailyLineScheduleActions.assignFailure({ error: message }),
              ToastActions.showToast({
                severity: 'error',
                summary: 'Daily line scheduling',
                detail: message,
              })
            );
          })
        )
      )
    )
  );

  setLineStatus$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DailyLineScheduleActions.setLineStatus),
      mergeMap(({ id, active }) =>
        this.api.setLineStatus(id, active).pipe(
          mergeMap((line) =>
            of(
              DailyLineScheduleActions.setLineStatusSuccess({ line }),
              DailyLineScheduleActions.refresh()
            )
          ),
          catchError((error) => {
            console.error(error);
            const message = userFacingApiError(error);
            return of(
              DailyLineScheduleActions.assignFailure({ error: message }),
              ToastActions.showToast({
                severity: 'error',
                summary: 'Daily line scheduling',
                detail: message,
              })
            );
          })
        )
      )
    )
  );

  setShiftStatus$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DailyLineScheduleActions.setShiftStatus),
      mergeMap(({ base, code, active }) =>
        this.api.setShiftStatus(base, code, active).pipe(
          mergeMap((shift) =>
            of(
              DailyLineScheduleActions.setShiftStatusSuccess({ base, shift }),
              DailyLineScheduleActions.refresh()
            )
          ),
          catchError((error) => {
            console.error(error);
            const message = userFacingApiError(error);
            return of(
              DailyLineScheduleActions.assignFailure({ error: message }),
              ToastActions.showToast({
                severity: 'error',
                summary: 'Daily line scheduling',
                detail: message,
              })
            );
          })
        )
      )
    )
  );

  assign$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DailyLineScheduleActions.assign),
      mergeMap(({ body }) =>
        this.api.assignShifts(body).pipe(
          mergeMap(() =>
            of(
              DailyLineScheduleActions.assignSuccess(),
              DailyLineScheduleActions.refresh(),
              ToastActions.showToast({
                severity: 'success',
                summary: 'Daily line scheduling',
                detail: 'Crew assigned',
              })
            )
          ),
          catchError((error: unknown) => {
            console.error(error);
            const message = userFacingApiError(error);
            const overlaps = overlapConflicts(error);
            if (overlaps.length > 0) {
              return of(
                DailyLineScheduleActions.assignFailure({ error: message, overlaps }),
                ToastActions.showToast({
                  severity: 'error',
                  summary: 'Daily line scheduling',
                  detail: message,
                })
              );
            }
            return of(
              DailyLineScheduleActions.assignFailure({ error: message }),
              ToastActions.showToast({
                severity: 'error',
                summary: 'Daily line scheduling',
                detail: message,
              })
            );
          })
        )
      )
    )
  );

  changeShift$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DailyLineScheduleActions.changeShift),
      mergeMap(({ body }) =>
        this.api.changeShifts(body).pipe(
          mergeMap(() =>
            of(
              DailyLineScheduleActions.changeShiftSuccess(),
              DailyLineScheduleActions.refresh(),
              ToastActions.showToast({
                severity: 'success',
                summary: 'Daily line scheduling',
                detail: 'Shift updated',
              })
            )
          ),
          catchError((error: unknown) => {
            console.error(error);
            const message = userFacingApiError(error);
            return of(
              DailyLineScheduleActions.changeShiftFailure({ error: message }),
              ToastActions.showToast({
                severity: 'error',
                summary: 'Daily line scheduling',
                detail: message,
              })
            );
          })
        )
      )
    )
  );
}

function overlapConflicts(error: unknown): DailyLineAllocationOverlapDto[] {
  if (!(error instanceof HttpErrorResponse) || error.status !== 409) {
    return [];
  }
  const body = error.error as { conflicts?: DailyLineAllocationOverlapDto[] } | undefined;
  return body?.conflicts ?? [];
}
